import React from 'react';
import variants from '../../assets/image-variants.json';

export interface ResponsiveImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'loading'> {
  src: string;
  alt: string;
  sizes?: string;
  width?: string | number;
  height?: string | number;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  /** Optional class for the wrapping <picture> element. Use this when layout sizing must live on the wrapper. */
  pictureClassName?: string;
}

type VariantKind = 'responsive' | 'mascot' | 'single';

const variantMap = variants as Record<string, VariantKind>;

interface PictureSources {
  avifSrcSet: string;
  webpSrcSet: string;
}

/**
 * Build <picture> source attributes for an imported image src.
 * - "responsive" basenames → 480w / 960w / 1600w srcset
 * - "mascot" basenames → single 600w src
 * - "single" basenames (or unknown) → base webp/avif next to the original
 *
 * Unknown basenames fall back to "single" rather than "responsive" so that a
 * missing manifest entry never points at a 404 (which would render a broken
 * image in browsers that commit to a chosen <source>).
 */
export function pictureSourcesFromImport(src: string): PictureSources {
  if (!src) return { avifSrcSet: '', webpSrcSet: '' };

  const filenameWithHash = src.split('/').pop() || '';
  const match = filenameWithHash.match(/^([^.]+?)(?:\.([a-f0-9]{8,20}))?\.(png|jpe?g)$/i);
  if (!match) {
    return { avifSrcSet: src, webpSrcSet: src };
  }

  const [, baseName, hash] = match;
  const pathPrefix = src.substring(0, src.lastIndexOf('/') + 1);
  const hashPart = hash ? `.${hash}` : '';
  const kind = variantMap[baseName] ?? 'single';

  if (kind === 'mascot') {
    return {
      avifSrcSet: `${pathPrefix}${baseName}-600w${hashPart}.avif`,
      webpSrcSet: `${pathPrefix}${baseName}-600w${hashPart}.webp`,
    };
  }

  if (kind === 'responsive') {
    const widths = [480, 960, 1600];
    return {
      avifSrcSet: widths
        .map((w) => `${pathPrefix}${baseName}-${w}w${hashPart}.avif ${w}w`)
        .join(', '),
      webpSrcSet: widths
        .map((w) => `${pathPrefix}${baseName}-${w}w${hashPart}.webp ${w}w`)
        .join(', '),
    };
  }

  // "single" — base file alongside the original
  return {
    avifSrcSet: `${pathPrefix}${baseName}${hashPart}.avif`,
    webpSrcSet: `${pathPrefix}${baseName}${hashPart}.webp`,
  };
}

// In dev mode (webpack-dev-server), the optimized .webp/.avif variants don't
// exist — they are copied into build/static/media only by scripts/optimize-images.js
// during the postbuild step. Rendering <source> in dev would 404, and browsers
// commit to a chosen <picture> source rather than falling back to <img>. So in
// dev we render the original PNG/JPG directly; production keeps the full pipeline.
const IS_DEV = process.env.NODE_ENV === 'development';

const ResponsiveImage = React.forwardRef<HTMLImageElement, ResponsiveImageProps>(({
  src,
  alt,
  sizes = '100vw',
  width,
  height,
  loading = 'lazy',
  fetchPriority,
  className,
  pictureClassName,
  ...rest
}, ref) => {
  if (IS_DEV) {
    return (
      <img
        ref={ref}
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        className={className}
        {...rest}
      />
    );
  }

  const { avifSrcSet, webpSrcSet } = pictureSourcesFromImport(src);

  // suppressHydrationWarning on <picture> + <img>: React 19 strict-hydrates
  // <picture>, but browsers may mutate <img>.currentSrc/srcset to the chosen
  // <source> between react-snap's serialization and client hydration. That
  // attribute drift triggers error #418 on routes with many lazy images even
  // though the visible UI is correct.
  return (
    <picture className={pictureClassName} suppressHydrationWarning>
      {avifSrcSet && <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />}
      {webpSrcSet && <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />}
      <img
        ref={ref}
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        className={className}
        suppressHydrationWarning
        {...rest}
      />
    </picture>
  );
});

export default ResponsiveImage;
